import * as DropdownMenuPrimitive from "@radix-ui/react-dropdown-menu"
import { FocusScope } from "@radix-ui/react-focus-scope"
import { clsx } from "clsx"
import { cloneElement, forwardRef, isValidElement, type ReactNode } from "react"

import { theme } from "../../theme"
import { ActionListItem } from "../ActionList"
import { useButtonGroup } from "../ButtonGroup"
import { Text } from "../Text"
import styles from "./DropdownMenu.module.css"

export interface DropdownMenuProps {
  /**
   * The controlled open state of the dropdown menu.
   * Must be used in conjunction with onOpenChange
   */
  open?: boolean
  /**
   * Event handler called when the open state of the dropdown menu changes.
   */
  onOpenChange?: (open: boolean) => void
  /**
   * When set to true, interaction with outside elements will be disabled.
   * Only menu content will be visible to screen readers
   */
  modal?: boolean
  children: ReactNode
}

export function DropdownMenu(props: DropdownMenuProps) {
  const { open, onOpenChange, modal = false, children } = props

  return (
    <DropdownMenuPrimitive.Root
      modal={modal}
      open={open}
      onOpenChange={onOpenChange}
    >
      {children}
    </DropdownMenuPrimitive.Root>
  )
}

interface DropdownMenuTriggerProps {
  disabled?: boolean
  children: ReactNode
}

export function DropdownMenuTrigger(props: DropdownMenuTriggerProps) {
  const buttonGroup = useButtonGroup()
  const { disabled = buttonGroup?.disabled, children, ...implicitProps } = props
  return (
    <DropdownMenuPrimitive.Trigger
      {...implicitProps}
      asChild
      disabled={disabled}
    >
      {children}
    </DropdownMenuPrimitive.Trigger>
  )
}

export interface DropdownMenuContentProps {
  align?: "center" | "end" | "start"
  side?: "bottom" | "left" | "right" | "top"
  sideOffset?: number
  minWidth?: 128 | 256 | "full-trigger-width"
  children: ReactNode
  asPortal?: boolean
  maxHeight?: 256 | 400
  /**
   * Whether the content should avoid collisions with the viewport
   * @default true
   */
  avoidCollisions?: boolean
  /**
   * The distance in pixels from the boundary edges where collision
   * detection should occur
   * @default 0
   */
  collisionPadding?: 0 | 8
  /**
   * Whether the content has a border
   * @default true
   */
  border?: boolean
  /**
   * When true, surrounds the dropdown content children with a radix primitive
   * focus scope. Ensures children can grab focus from
   * dropdown content when needed. https://github.com/radix-ui/primitives/issues/922#issuecomment-948366132
   * @default false
   */
  childrenFocusScope?: boolean
}

export const DropdownMenuContent = forwardRef<
  HTMLDivElement,
  DropdownMenuContentProps
>(function DropdownMenuContent(props, ref) {
  const {
    minWidth = 128,
    maxHeight,
    asPortal = true,
    avoidCollisions,
    collisionPadding = 0,
    children,
    align,
    side,
    sideOffset,
    border = true,
    childrenFocusScope = false,
    ...implicitProps
  } = props

  const childrenWithFocusScope = (
    <FocusScope asChild loop trapped>
      {children}
    </FocusScope>
  )

  const content = (
    <DropdownMenuPrimitive.Content
      {...implicitProps}
      className={clsx(
        styles.content,
        styles[`content-width-${minWidth}`],
        maxHeight && styles[`content-height-${maxHeight}`],
        border && styles.border,
      )}
      sideOffset={sideOffset || parseFloat(theme.space[4])}
      avoidCollisions={avoidCollisions}
      collisionPadding={collisionPadding}
      align={align}
      side={side}
      /* Scroll event propagation is stopped to fix scroll inside dialogs
      https://github.com/prismicio/editor/pull/1164
      https://github.com/radix-ui/primitives/issues/1159 */
      onWheel={(event) => event.stopPropagation()}
      ref={ref}
    >
      {childrenFocusScope ? childrenWithFocusScope : children}
    </DropdownMenuPrimitive.Content>
  )

  if (!asPortal) return content

  return <DropdownMenuPrimitive.Portal>{content}</DropdownMenuPrimitive.Portal>
})

interface DropdownMenuItemProps {
  children: ReactNode
  disabled?: boolean
  onSelect?: (event: Event) => void
  textValue?: string
  endIcon?: ReactNode
  startIcon?: ReactNode
  color?: "grey" | "tomato" | undefined
  description?: ReactNode
  readOnly?: boolean
  endAdornment?: ReactNode
  /**
   * For progress step use-case,
   * mandatory to pass the boolean value for each item, even if `false`.
   */
  completed?: boolean

  onMouseOverChange?: (isMouseOver: boolean) => void
  renderTooltip?: (node: ReactNode) => ReactNode
}

type IconProps = Readonly<{
  className?: string
}>

export const DropdownMenuItem = forwardRef<
  HTMLDivElement,
  DropdownMenuItemProps
>(function DropdownMenuItem(props, ref) {
  const {
    children,
    endIcon,
    startIcon,
    disabled,
    color = "grey",
    description,
    onMouseOverChange,
    onSelect,
    textValue,
    readOnly = false,
    renderTooltip = (node) => node,
    endAdornment,
    completed,
    ...implicitProps
  } = props

  const dropdownMenuPrimitiveItemBaseProps: DropdownMenuPrimitive.DropdownMenuItemProps =
    {
      disabled,
      ...implicitProps,
      onSelect,
      textValue,
      onMouseEnter: onMouseOverChange && (() => onMouseOverChange(true)),
      onMouseLeave: onMouseOverChange && (() => onMouseOverChange(false)),
    }

  const startIconElement = isValidElement<IconProps>(startIcon)
    ? cloneElement(startIcon, {
        className: clsx(startIcon.props.className, styles.itemIcon),
      })
    : null
  const endIconElement = isValidElement<IconProps>(endIcon)
    ? cloneElement(endIcon, {
        className: clsx(endIcon.props.className, styles.itemIcon),
      })
    : null

  if (description) {
    return (
      <>
        {renderTooltip(
          <DropdownMenuPrimitive.Item
            {...dropdownMenuPrimitiveItemBaseProps}
            ref={ref}
            asChild
          >
            <ActionListItem
              color={color}
              description={description}
              disabled={disabled}
              endAdornment={endAdornment}
              renderStartIcon={
                startIconElement ? () => startIconElement : undefined
              }
              renderEndIcon={endIconElement ? () => endIconElement : undefined}
              completed={completed}
              readOnly={readOnly}
              textVariant="bold"
            >
              {children}
            </ActionListItem>
          </DropdownMenuPrimitive.Item>,
        )}
      </>
    )
  }

  return (
    <>
      {renderTooltip(
        <DropdownMenuPrimitive.Item
          {...dropdownMenuPrimitiveItemBaseProps}
          ref={ref}
          className={clsx(styles.item, styles[`item-color-${color}`])}
        >
          {startIconElement}

          <span className={styles.itemText}>
            <Text component="span" color={mainTextColor[color]}>
              {children}
            </Text>
          </span>

          {endIconElement}

          {endAdornment ?? null}
        </DropdownMenuPrimitive.Item>,
      )}
    </>
  )
})

interface DropdownMenuLabelProps {
  children: ReactNode
}

export function DropdownMenuLabel({ children }: DropdownMenuLabelProps) {
  return (
    <DropdownMenuPrimitive.Label className={styles.label}>
      <Text component="span">{children}</Text>
    </DropdownMenuPrimitive.Label>
  )
}

const mainTextColor = {
  grey: "grey12",
  tomato: "tomato11",
} as const
